/**
 * The pages: one per item kind, plus the home page.
 *
 * Each page reads one head item and, where the item has lists, its pages, and
 * renders what the item carries and nothing it does not: a page cannot show a
 * fact the Serve stage did not compute from the rows, which is how the site
 * stays a projection of the graph. The coalition page draws the computed
 * overlay with its recipe, version, parameters, window, and run in view
 * (invariant 14), and calls it what it is: shared funding, not a label.
 */
import { useState, type FormEvent } from 'react';
import type { CoalitionMapItem } from '../../src/serve/items.js';
import { api } from './api.js';
import { Badge, Empty, GrantRow, Link, Loading, Pager, PartyLink, Problem, Section } from './components.js';
import { formatAmount, formatCount, formatEin, formatPeriod, sourceLabel } from './format.js';
import { navigate } from './router.js';
import { useQuery } from './useQuery.js';

const TOP_SUBJECTS: readonly [string, string][] = [
  ['sa000000', 'Arts and culture'],
  ['sb000000', 'Education'],
  ['sc000000', 'Environment'],
  ['sd000000', 'Philanthropy'],
  ['se000000', 'Health'],
  ['sf000000', 'Science'],
  ['sg000000', 'Social sciences'],
  ['sh000000', 'Information and communications'],
  ['sj000000', 'Public safety and disaster management'],
  ['sk000000', 'Public affairs'],
  ['sm000000', 'Agriculture, fishing and forestry'],
  ['sn000000', 'Community and economic development'],
  ['sp000000', 'Religion'],
  ['sq000000', 'Sports and recreation'],
  ['sr000000', 'International human rights'],
  ['ss000000', 'Human services'],
  ['st000000', 'International relations'],
];

export const HomePage = () => {
  const [ein, setEin] = useState('');
  const build = useQuery('build', () => api.build());
  const submit = (event: FormEvent) => {
    event.preventDefault();
    const digits = ein.replace(/\D/g, '');
    if (digits.length === 9) {
      navigate(`/org/${digits}`);
    }
  };
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight">Who funds what</h1>
      <p className="mt-2 max-w-2xl text-stone-700">
        A knowledge graph of the United States nonprofit funding landscape, built from public filings. Every organization that files its own return has a page; every grant edge names the filing,
        page, or award it came from.
      </p>
      <form onSubmit={submit} className="mt-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="block text-sm text-stone-600">Find an organization by EIN</span>
          <input
            name="ein"
            value={ein}
            onChange={(event) => setEin(event.target.value)}
            placeholder="52-2094677"
            className="mt-1 w-56 rounded border border-stone-300 px-2 py-1 font-mono"
            inputMode="numeric"
            aria-label="EIN"
          />
        </label>
        <button type="submit" className="rounded bg-teal-800 px-3 py-1.5 text-white">
          Open
        </button>
      </form>
      <Section title="Subjects">
        <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3">
          {TOP_SUBJECTS.map(([slug, label]) => (
            <li key={slug}>
              <Link to={`/subject/${slug}`}>{label}</Link>
            </li>
          ))}
        </ul>
      </Section>
      <Section title="This build">
        {build.loading ? <Loading what="the build" /> : null}
        {build.error !== null ? <Problem error={build.error} /> : null}
        {build.value !== null ? (
          <p className="text-sm text-stone-600" data-testid="build">
            Gold run <code>{build.value.goldRunId}</code>; {formatCount(build.value.counts['organization'] ?? 0)} organization pages, {formatCount(build.value.counts['subject'] ?? 0)} subjects,{' '}
            {formatCount(build.value.counts['coalition-map'] ?? 0)} coalition maps.
          </p>
        ) : null}
      </Section>
    </>
  );
};

const GrantList = ({ ein, list, side, pages }: { ein: string; list: 'grantsReceived' | 'grantsGiven'; side: 'funder' | 'recipient'; pages: number }) => {
  const [page, setPage] = useState(1);
  const result = useQuery(`${list}:${ein}:${page}`, () => (list === 'grantsReceived' ? api.grantsReceived(ein, page) : api.grantsGiven(ein, page)));
  if (pages === 0) {
    return <Empty>None in the graph.</Empty>;
  }
  if (result.loading) {
    return <Loading what="grants" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  if (result.value === null) {
    return <Empty>This page is not in the graph.</Empty>;
  }
  return (
    <>
      <ul data-testid={list}>
        {result.value.edges.map((edge) => (
          <GrantRow key={edge.id} edge={edge} side={side} />
        ))}
      </ul>
      <Pager page={result.value.page} pages={result.value.pages} onPage={setPage} />
    </>
  );
};

export const OrganizationPage = ({ ein }: { ein: string }) => {
  const result = useQuery(`organization:${ein}`, () => api.organization(ein));
  if (result.loading) {
    return <Loading what="the organization" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const item = result.value;
  if (item === null) {
    return (
      <>
        <h1 className="text-2xl font-semibold">No page for {formatEin(ein)}</h1>
        <p className="mt-2 text-stone-700">
          An organization has its own page when it files its own return. This number is not in the graph, or names a group that appears only inside another organization's filing.
        </p>
      </>
    );
  }
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="organization-name">
        {item.name ?? formatEin(item.ein)}
      </h1>
      <p className="mt-1 text-stone-600">
        EIN {formatEin(item.ein)}
        {item.city !== null || item.state !== null ? ` · ${[item.city, item.state].filter((part) => part !== null).join(', ')}` : ''}
        {item.subject !== null ? (
          <>
            {' · '}
            <Link to={`/subject/${item.subject.code.toLowerCase()}`}>{item.subject.label}</Link>
            {item.nteeCode !== null ? <span className="text-xs"> (NTEE {item.nteeCode})</span> : null}
          </>
        ) : null}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {item.sources.map((source) => (
          <Badge key={source}>{sourceLabel(source)}</Badge>
        ))}
        {item.funder ? (
          <Link to={`/funder/${item.ein}`} className="text-sm text-teal-800 underline">
            Funder profile
          </Link>
        ) : null}
        {item.coalitionMap !== null ? (
          <Link to={`/coalition/${item.ein}`} className="text-sm text-teal-800 underline">
            Coalition map
          </Link>
        ) : null}
      </div>

      {item.mission !== null ? (
        <Section title="Mission" aside={`As filed, ${formatPeriod(item.mission.taxPeriod)}`}>
          <p className="text-stone-800">{item.mission.text}</p>
        </Section>
      ) : null}

      {item.programs.length > 0 ? (
        <Section title="Programs" aside={`As filed, ${formatPeriod(item.programs[0]?.taxPeriod ?? null)}`}>
          <ul className="list-disc space-y-2 pl-5 text-stone-800">
            {item.programs.map((program, index) => (
              <li key={index}>{program.text}</li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title="Filings" aside={`${formatCount(item.filings.length)} in the graph`}>
        {item.filings.length === 0 ? (
          <Empty>No e-filed return in the graph{item.masterFile?.latestTaxPeriod ? `; the master file records a return for ${formatPeriod(item.masterFile.latestTaxPeriod)}` : ''}.</Empty>
        ) : (
          <table className="w-full text-sm" data-testid="filings">
            <thead className="text-left text-stone-500">
              <tr>
                <th className="py-1 font-normal">Return</th>
                <th className="py-1 font-normal">Period</th>
                <th className="py-1 font-normal">Object</th>
                <th className="py-1 font-normal">Source</th>
              </tr>
            </thead>
            <tbody>
              {item.filings.map((filing) => (
                <tr key={filing.id} className="border-t border-stone-100">
                  <td className="py-1">
                    {filing.returnType ?? '—'}
                    {filing.amended ? ' (amended)' : ''}
                    {!filing.isCurrent ? ' (superseded)' : ''}
                  </td>
                  <td className="py-1">{formatPeriod(filing.taxPeriod)}</td>
                  <td className="py-1 font-mono text-xs">{filing.objectId}</td>
                  <td className="py-1 text-stone-600">{sourceLabel(filing.source)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {item.masterFile !== null ? (
        <Section title="Master file" aside={sourceLabel('irs-bmf')}>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
            <dt className="text-stone-500">Subsection</dt>
            <dd>{item.masterFile.subsection ?? '—'}</dd>
            <dt className="text-stone-500">Foundation code</dt>
            <dd>{item.masterFile.foundationCode ?? '—'}</dd>
            <dt className="text-stone-500">Ruling</dt>
            <dd>{item.masterFile.rulingDate ?? '—'}</dd>
            <dt className="text-stone-500">Latest return</dt>
            <dd>{formatPeriod(item.masterFile.latestTaxPeriod)}</dd>
            <dt className="text-stone-500">Assets</dt>
            <dd>{formatAmount(item.masterFile.assetAmount)}</dd>
            <dt className="text-stone-500">Income</dt>
            <dd>{formatAmount(item.masterFile.incomeAmount)}</dd>
            <dt className="text-stone-500">Revenue</dt>
            <dd>{formatAmount(item.masterFile.revenueAmount)}</dd>
          </dl>
        </Section>
      ) : null}

      <Section title="Grants received" aside={`${formatCount(item.grantsReceived.count)} · ${formatAmount(item.grantsReceived.total)}`}>
        <GrantList ein={item.ein} list="grantsReceived" side="funder" pages={item.grantsReceived.pages} />
      </Section>

      <Section title="Grants given" aside={`${formatCount(item.grantsGiven.count)} · ${formatAmount(item.grantsGiven.total)}`}>
        <GrantList ein={item.ein} list="grantsGiven" side="recipient" pages={item.grantsGiven.pages} />
      </Section>

      {item.sponsoredProjects.length > 0 ? (
        <Section title="Sponsored projects" aside="Named in this organization's own filings">
          <ul className="space-y-1 text-sm" data-testid="projects">
            {item.sponsoredProjects.map((project) => (
              <li key={project.id}>
                {project.name} <span className="text-stone-500">· {formatCount(project.grants)} grant rows</span>
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-stone-500">A sponsored project is shown here, in its sponsor's context, until it files its own return.</p>
        </Section>
      ) : null}
    </>
  );
};

export const FunderPage = ({ ein }: { ein: string }) => {
  const result = useQuery(`funder:${ein}`, () => api.funder(ein));
  if (result.loading) {
    return <Loading what="the funder" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const item = result.value;
  if (item === null) {
    return <h1 className="text-2xl font-semibold">No funder profile for {formatEin(ein)}</h1>;
  }
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="funder-name">
        {item.name ?? formatEin(item.ein)}
      </h1>
      <p className="mt-1 text-stone-600">
        Funder profile · <Link to={`/org/${item.ein}`}>organization page</Link> · {formatCount(item.grantsGiven.count)} grants · {formatAmount(item.grantsGiven.total)}
      </p>
      <Section title="By period">
        <table className="text-sm">
          <tbody>
            {item.periods.map((period) => (
              <tr key={period.period} className="border-t border-stone-100">
                <td className="py-1 pr-6">{formatPeriod(period.period)}</td>
                <td className="py-1 pr-6 tabular-nums">{formatCount(period.count)} grants</td>
                <td className="py-1 tabular-nums">{formatAmount(period.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
      <Section title="Subjects funded" aside="By the recipients' master-file classification">
        {item.subjects.length === 0 ? (
          <Empty>No recipient with a classification in the graph.</Empty>
        ) : (
          <ul className="space-y-1 text-sm">
            {item.subjects.map((subject) => (
              <li key={subject.code}>
                <Link to={`/subject/${subject.code.toLowerCase()}`}>{subject.label}</Link> · {formatCount(subject.count)} grants · {formatAmount(subject.total)}
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="States funded">
        {item.states.length === 0 ? (
          <Empty>No recipient with a state in the graph.</Empty>
        ) : (
          <ul className="flex flex-wrap gap-2 text-sm">
            {item.states.map((state) => (
              <li key={state.state}>
                <Badge>
                  {state.state} · {formatCount(state.count)}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title="Recipients">
        <p className="text-sm text-stone-700">
          {formatCount(item.recipients.listed)} to organizations with a page, {formatCount(item.recipients.unlisted)} to organizations with a number and no return on record,{' '}
          {formatCount(item.recipients.names)} to names the graph has not placed.
        </p>
      </Section>
      <Section title="Funders that share recipients" aside="Peers by shared recipients, stated or resolved">
        {item.peers.length === 0 ? (
          <Empty>No other funder in the graph reached the same organizations.</Empty>
        ) : (
          <ul className="space-y-1 text-sm" data-testid="peers">
            {item.peers.map((peer) => (
              <li key={peer.ein}>
                <Link to={`/funder/${peer.ein}`}>{peer.name ?? formatEin(peer.ein)}</Link> · {formatCount(peer.sharedRecipients)} shared
              </li>
            ))}
          </ul>
        )}
      </Section>
    </>
  );
};

const SubjectFunders = ({ slug, states }: { slug: string; states: readonly { state: string; count: number; pages: number }[] }) => {
  const [state, setState] = useState(states[0]?.state ?? '');
  const [page, setPage] = useState(1);
  const [year, setYear] = useState('');
  const result = useQuery(`subjectFunders:${slug}:${state}:${page}`, () => (state === '' ? Promise.resolve(null) : api.subjectFunders(slug, state, page)));
  if (states.length === 0) {
    return <Empty>No funder of work under this subject in the graph.</Empty>;
  }
  const funders = (result.value?.funders ?? []).filter((funder) => year === '' || funder.years.includes(year));
  return (
    <>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label>
          <span className="block text-stone-600">State</span>
          <select
            value={state}
            onChange={(event) => {
              setState(event.target.value);
              setPage(1);
            }}
            className="mt-1 rounded border border-stone-300 px-2 py-1"
            aria-label="State"
          >
            {states.map((entry) => (
              <option key={entry.state} value={entry.state}>
                {entry.state} · {formatCount(entry.count)} funders
              </option>
            ))}
          </select>
        </label>
        <label>
          <span className="block text-stone-600">Tax year</span>
          <input value={year} onChange={(event) => setYear(event.target.value)} placeholder="any" className="mt-1 w-24 rounded border border-stone-300 px-2 py-1" aria-label="Tax year" />
        </label>
      </div>
      {result.loading ? <Loading what="funders" /> : null}
      {result.error !== null ? <Problem error={result.error} /> : null}
      {result.value !== null ? (
        <>
          <ul className="mt-3 space-y-1 text-sm" data-testid="subject-funders">
            {funders.map((funder) => (
              <li key={funder.id}>
                {funder.ein === null ? <span>{funder.name ?? funder.id}</span> : <Link to={`/funder/${funder.ein}`}>{funder.name ?? formatEin(funder.ein)}</Link>} · {formatCount(funder.grants)}{' '}
                grants · {formatAmount(funder.total)} · {funder.years.join(', ')}
              </li>
            ))}
          </ul>
          <Pager page={result.value.page} pages={result.value.pages} onPage={setPage} />
        </>
      ) : null}
    </>
  );
};

export const SubjectPage = ({ slug }: { slug: string }) => {
  const result = useQuery(`subject:${slug}`, () => api.subject(slug));
  if (result.loading) {
    return <Loading what="the subject" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const item = result.value;
  if (item === null) {
    return <h1 className="text-2xl font-semibold">No subject {slug}</h1>;
  }
  return (
    <>
      <p className="text-sm text-stone-500">
        {item.broader !== null ? (
          <>
            <Link to={`/subject/${item.broader.code.toLowerCase()}`}>{item.broader.label}</Link> ›{' '}
          </>
        ) : (
          'Subject › '
        )}
        <span>{item.code}</span>
      </p>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="subject-label">
        {item.label}
      </h1>
      <p className="mt-2 max-w-3xl text-stone-700">{item.definition}</p>
      <p className="mt-2 text-sm text-stone-600">{formatCount(item.organizations)} organizations in the master file are classified here or beneath.</p>
      {item.narrower.length > 0 ? (
        <Section title="Narrower subjects">
          <ul className="grid grid-cols-1 gap-1 text-sm sm:grid-cols-2" data-testid="narrower">
            {item.narrower.map((child) => (
              <li key={child.code}>
                <Link to={`/subject/${child.code.toLowerCase()}`}>{child.label}</Link>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      {item.nteeCodes.length > 0 ? (
        <Section title="NTEE codes mapped here">
          <ul className="flex flex-wrap gap-2 text-sm">
            {item.nteeCodes.map((code) => (
              <li key={code.code}>
                <Badge>
                  {code.code} {code.name}
                </Badge>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
      <Section title="Who funds work under this subject" aside="Including narrower subjects; by the recipient's state">
        <SubjectFunders slug={slug} states={item.funders} />
      </Section>
    </>
  );
};

/** The map: the anchor in the middle, its funders on an inner ring, the members on an outer ring, a line for each shared funder. */
const CoalitionDrawing = ({ map }: { map: CoalitionMapItem }) => {
  const size = 640;
  const centre = size / 2;
  const funderRadius = 120;
  const memberRadius = 280;
  const funderAt = new Map(
    map.funders.map((funder, index) => {
      const angle = (2 * Math.PI * index) / Math.max(map.funders.length, 1) - Math.PI / 2;
      return [funder.ein ?? '', { x: centre + funderRadius * Math.cos(angle), y: centre + funderRadius * Math.sin(angle), funder }] as const;
    }),
  );
  const members = map.members.map((member, index) => {
    const angle = (2 * Math.PI * index) / Math.max(map.members.length, 1) - Math.PI / 2;
    return { member, x: centre + memberRadius * Math.cos(angle), y: centre + memberRadius * Math.sin(angle) };
  });
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-2xl" role="img" aria-label={`Coalition map around ${map.anchor.name ?? map.anchor.ein ?? 'the anchor'}`}>
      {members.map(({ member, x, y }) =>
        member.sharedFunders.map((ein) => {
          const hub = funderAt.get(ein);
          return hub === undefined ? null : <line key={`${member.organization.ein}-${ein}`} x1={hub.x} y1={hub.y} x2={x} y2={y} stroke="#99f6e4" strokeWidth={1} />;
        }),
      )}
      {[...funderAt.values()].map(({ x, y, funder }) => (
        <g key={funder.ein}>
          <line x1={centre} y1={centre} x2={x} y2={y} stroke="#0f766e" strokeWidth={1.5} />
          <circle cx={x} cy={y} r={6} fill="#0f766e" />
          <text x={x} y={y - 10} textAnchor="middle" fontSize={10} fill="#134e4a">
            {(funder.name ?? funder.ein ?? '').slice(0, 28)}
          </text>
        </g>
      ))}
      {members.map(({ member, x, y }) => (
        <g key={member.organization.ein}>
          <circle cx={x} cy={y} r={3 + Math.min(member.score, 6)} fill="#a8a29e" />
          <text x={x} y={y + 16} textAnchor="middle" fontSize={9} fill="#44403c">
            {(member.organization.name ?? member.organization.ein ?? '').slice(0, 26)}
          </text>
        </g>
      ))}
      <circle cx={centre} cy={centre} r={10} fill="#1c1917" />
      <text x={centre} y={centre - 16} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1c1917">
        {(map.anchor.name ?? map.anchor.ein ?? '').slice(0, 30)}
      </text>
    </svg>
  );
};

export const CoalitionPage = ({ lookup, label }: { lookup: () => Promise<CoalitionMapItem | null>; label: string }) => {
  const result = useQuery(`coalition:${label}`, lookup);
  if (result.loading) {
    return <Loading what="the coalition map" />;
  }
  if (result.error !== null) {
    return <Problem error={result.error} />;
  }
  const map = result.value;
  if (map === null) {
    return (
      <>
        <h1 className="text-2xl font-semibold">No coalition map for {label}</h1>
        <p className="mt-2 text-stone-700">The co-funding recipe found no organization sharing two or more funders with this one in the window, or the anchor is not in the graph.</p>
      </>
    );
  }
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight" data-testid="coalition-anchor">
        Coalition map around <PartyLink party={map.anchor} />
      </h1>
      <p className="mt-1 text-sm text-stone-600">
        A computed overlay, not a label: organizations that share two or more funders with the anchor, from grants in tax years {map.window}. Recipe <code>{map.recipe}</code> version{' '}
        {map.version}, parameters <code>{map.parameters}</code>, run <code>{map.run}</code>.
      </p>
      <div className="mt-4">
        <CoalitionDrawing map={map} />
      </div>
      <Section title="Funders that reached the anchor" aside={`${formatCount(map.funders.length)} in the window`}>
        <ul className="space-y-1 text-sm">
          {map.funders.map((funder) => (
            <li key={funder.ein}>
              <PartyLink party={funder} />
            </li>
          ))}
        </ul>
      </Section>
      {map.excluded.length > 0 ? (
        <Section title="Reached the anchor, not counted" aside="Above the recipe's fan-out ceiling">
          <ul className="space-y-1 text-sm" data-testid="excluded">
            {map.excluded.map((funder) => (
              <li key={funder.ein}>
                <PartyLink party={funder} />
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-stone-500">A funder that reaches more organizations than the ceiling shares recipients with everyone; it is shown and not scored.</p>
        </Section>
      ) : null}
      <Section title="Members" aside="Score is the number of shared funders">
        <ul className="space-y-2 text-sm" data-testid="members">
          {map.members.map((member) => (
            <li key={member.organization.ein}>
              <PartyLink party={member.organization} /> <Badge tone="teal">{formatCount(member.score)} shared</Badge>
              <div className="text-xs text-stone-500">
                Shared: {member.sharedFunders.map((ein) => map.funders.find((funder) => funder.ein === ein)?.name ?? formatEin(ein)).join('; ')}
              </div>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
};

export const NotFoundPage = ({ path }: { path: string }) => (
  <>
    <h1 className="text-2xl font-semibold">Nothing at {path}</h1>
    <p className="mt-2 text-stone-700">
      Pages are <code>/org/&#123;ein&#125;</code>, <code>/funder/&#123;ein&#125;</code>, <code>/subject/&#123;slug&#125;</code>, and <code>/coalition/&#123;ein&#125;</code>.
    </p>
  </>
);
